import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatHour } from '../utils/formatTime';

const SaveSpinner = () => (
  <span className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/60">
    <svg
      className="w-3 h-3 animate-spin text-gray-400"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
    </svg>
  </span>
);

function ShiftCell({ shift, isSaving, templateMap, onClick, muted, disabled, disabledTitle }) {
  const { t } = useTranslation('scheduler');

  const handleClick = (e) => {
    if (disabled) return;
    onClick(e.currentTarget.getBoundingClientRect());
  };

  if (!shift) {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        title={disabled ? disabledTitle : undefined}
        className="w-full flex items-center justify-center h-7 rounded-lg border-2 border-dashed border-gray-200 text-xs text-gray-300 font-medium select-none hover:border-orange-300 hover:text-orange-400 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-200 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-200 disabled:hover:text-gray-300"
      >
        {t('grid.addCell')}
      </button>
    );
  }

  if (shift.isOff) {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        title={disabled ? disabledTitle : undefined}
        className="relative w-full flex items-center justify-center h-7 rounded-lg bg-gray-100 text-xs text-gray-500 font-medium select-none hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-100"
      >
        {isSaving && <SaveSpinner />}
        <span>{t('cell.off')}</span>
      </button>
    );
  }

  const template = shift.shiftTemplateId ? templateMap.get(shift.shiftTemplateId) : null;
  const color = template?.colorHex ?? '#6B7280';
  const label =
    shift.shiftTemplateLabel ||
    (shift.startTime && shift.endTime
      ? `${formatHour(shift.startTime)}–${formatHour(shift.endTime)}`
      : '?');

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      title={disabled ? disabledTitle : muted ? t('cell.needsCoverTooltip') : undefined}
      className={`relative w-full flex items-center justify-center h-7 rounded-lg text-xs font-semibold select-none transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:cursor-not-allowed ${
        disabled ? 'opacity-50' : muted ? 'opacity-45 hover:opacity-60' : 'hover:opacity-85'
      }`}
      style={{
        backgroundColor: `${color}33`,
        border: `1.5px solid ${color}`,
        color,
      }}
    >
      {isSaving && <SaveSpinner />}
      <span className={`truncate px-1 ${muted ? 'line-through decoration-2' : ''}`}>{label}</span>
    </button>
  );
}

export default ShiftCell;
